import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { GripVertical, Zap } from "lucide-react";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "./create.scss";
import { RootState } from "../../store";
import { connect, ConnectedProps } from "react-redux";
import { callRegisterMediaOnCms } from "@/store/upload/upload.action";
import {
  getMediaDimensions,
  computeAspectRatioAndOrientation,
} from "@/utils/createOptionsUtils";
import { Button, toast } from "..";
import {
  setUploadedSuccessfully,
  updateFileProgress,
  setUploadingError,
  decreaseCounter,
} from "@/store/upload/upload.slice";
import EmbedForm from "./EmbedForm";
import { BulkUploadForm, BulkMediaItem } from "./BulkUploadForm";
import { CustomUploadForm } from "./CustomUploadForm";
import SimuliveForm from "./SimuliveForm";
import TextToSpeechForm, { TTS_SERVICES } from "./TextToSpeechForm";
import PremiereForm from "./PremiereForm";
import {
  getUpdateMedia,
  getMedia,
  updateIsFilter,
  updateActiveCustomUploadTab,
} from "@/store/media/media.action";
import {
  emptyAssetDetails,
  emptyMediaDetails,
} from "@/store/media/media.slice";
import { setisFilter, setMediaList } from "@/store/media/media.slice";
import { updateMediaStatus } from "@/store/media/media.slice";
import { setSoftRefresh, showUploadProgress } from "@/store/app/app.slice";
import { setCreateBucketOrder } from "@/store/user/user.slice";
import { Uploader } from "..";
import { CREATE_OPTIONS } from "@/utils/constants/uiConstants";
import { resolveBucketEntries, BucketEntry } from "./createBucketEntries";
import { BucketModel } from "@/database";
import { BucketCard } from "./BucketCard";
import { trackCreate } from "@/utils/analytics";
import {
  CreateUserPreferenceApi,
  UpdateUserPreferenceApi,
} from "@/database/user/api";

interface SortableCardProps {
  id: string;
  disabled?: boolean;
  children: React.ReactNode;
}

const SortableCard: React.FC<SortableCardProps> = ({
  id,
  disabled,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: "relative",
    touchAction: "none",
  };

  return (
    <div ref={setNodeRef} style={style} className="create-card-wrap">
      {!disabled && (
        <div
          className="drag-hint-icon"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
          style={{
            position: "absolute",
            top: 6,
            left: 6,
            zIndex: 2,
            padding: 3,
            borderRadius: 4,
            color: "#adb5bd",
            background: "transparent",
            lineHeight: 0,
            cursor: "grab",
            opacity: 0,
            transition: "opacity 0.15s ease",
          }}
        >
          <GripVertical size={12} />
        </div>
      )}
      {children}
    </div>
  );
};

export const CreateOptions = ({
  closeModal,
  updateModalContent,
  updateModalFooter,
  onPremiereNextDisabledChange,
  onPremiereRpcPendingChange,
  onTtsNextDisabledChange,
  onTtsRpcPendingChange,
  callRegisterMediaOnCms,
  updateFileProgress,
  showUploadProgress,
  setUploadedSuccessfully,
  setSoftRefresh,
  decreaseCounter,
  openUpload,
  openPremiereEditor,
  onBulkUploadsStarted,
  getMedia,
  emptyMediaDetails,
  emptyAssetDetails,
  activeCustomUploadTab,
  updateActiveCustomUploadTab,
  updateMediaStatus,
  initialOption,
  onAssetTypeChange,
  adminBucketList,
  defaultTeam,
  createBucketOrder,
  setCreateBucketOrder,
  externalSearchQuery = "",
}: Props) => {
  const uploadingRef = useRef(false);
  const uploadingRefId = useRef("");
  const [modalTitle, setModalTitle] = useState<string>("");
  const [modalContent, setModalContent] = useState<React.ReactNode | null>(
    null
  );

  const [mediaId, setMediaId] = useState("");
  const [modalFooter, setModalFooter] = useState<React.ReactNode | null>(null);
  const [modalBtnDisabled, setModalBtnDisabled] = useState<boolean>(false);
  const [footerText, setFooterText] = useState<string>("Next");
  const [isValidatingUrl, setIsValidatingUrl] = useState<boolean>(false);
  const [hasFormChanges, setHasFormChanges] = useState<boolean>(false);

  const userBucketsList = useMemo(
    () => (adminBucketList ?? []).filter((b: BucketModel) => b.status === 1),
    [adminBucketList]
  );

  const hasPreferenceRef = useRef<boolean>(
    Array.isArray(createBucketOrder) && createBucketOrder.length > 0
  );
  const pendingSaveRef = useRef<number>(0);
  const searchQuery = externalSearchQuery;
  const visibleOrderRef = useRef<{ id: string }[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const visible = visibleOrderRef.current;
    const oldIdx = visible.findIndex((it) => it.id === active.id);
    const newIdx = visible.findIndex((it) => it.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const newIds = arrayMove(
      visible.map((it) => it.id),
      oldIdx,
      newIdx
    );
    setCreateBucketOrder(newIds);
    const saveId = ++pendingSaveRef.current;
    const save = hasPreferenceRef.current
      ? UpdateUserPreferenceApi(newIds)
      : CreateUserPreferenceApi(newIds).then((res) => {
          if (saveId === pendingSaveRef.current)
            hasPreferenceRef.current = true;
          return res;
        });
    save
      .then((res) => {
        if (saveId !== pendingSaveRef.current) return;
        if (res?.error) toast.fail("Failed to save order");
      })
      .catch(() => {
        if (saveId !== pendingSaveRef.current) return;
        toast.fail("Failed to save order");
      });
  };

  const sendCancelUploadRequest = useCallback(() => {
    if (!uploadingRefId.current) return;
    const apiUrl = process.env.NX_APP_MEDIA_CANCEL;
    const formData = new FormData();
    formData.append("status", "-2");
    formData.append("id", uploadingRefId.current);
    updateMediaStatus({ id: uploadingRefId.current, status: -2 });
    const sent = navigator.sendBeacon(apiUrl ?? "", formData);
  }, [updateMediaStatus]);

  const onFileEvent = useCallback(
    (event: string, data: any, cb?: any): any => {
      showUploadProgress();
      if (event === "fileAdded") {
        const file = data.file;
        file.isShorts = data.isShorts;
        file.isCampaign = data.id == "campaign";
        file.type = "media";
        file.name = "media";
        file.asset_type = data.asset_type;
        file.id = data.id;
        if (data.metaPreset) {
          file.preset_config = {
            meta: data.metaPreset,
            social: data.socialPreset,
          };
        }
        uploadingRef.current = true;

        getMediaDimensions(file.file ?? file).then(({ width, height }) => {
          const { aspect_ratio, orientation } =
            computeAspectRatioAndOrientation(width, height);
          file.aspect_ratio = aspect_ratio;
          file.orientation = orientation;
          if (data.asset_type === "video" && orientation === 2) {
            file.asset_type = "shorts";
            file.isShorts = true;
            file.is_short = 1;
          }

          callRegisterMediaOnCms(file, (response: any) => {
            if (response && response.status === 200) {
              emptyMediaDetails();
              emptyAssetDetails();
              if (
                response?.data?.preset_config?.meta &&
                response?.data?.preset_config?.meta?.length > 0
              ) {
                localStorage.setItem(
                  "metaPresetId",
                  response?.data?.preset_config?.meta
                );
              }
              if (
                response?.data?.preset_config?.social &&
                response?.data?.preset_config?.social?.length > 0
              ) {
                localStorage.setItem(
                  "socialPresetId",
                  response?.data?.preset_config?.social
                );
              }
              getMedia({ id: response.data.id });
              setMediaId(response.data.id);
              uploadingRefId.current = response?.data?.id;
              const id = response.data.id;
              const token = response.data.jwt;
              const query = {
                entity: id,
                key: "media",
                method: "asset.file",
              };
              openUpload();
              cb && cb(query, token);
            } else {
              toast.fail(response.error);
              closeModal();
            }
          });
        });
      } else if (event === "fileProgress") {
        const file = data.file;
        const progress = Math.round(file.progress(false) * 1000) / 10;
        updateFileProgress({
          identifier: file.uniqueIdentifier,
          progress: progress,
        });
      } else if (event === "fileSuccess") {
        decreaseCounter();
        uploadingRef.current = false;
        setUploadedSuccessfully({
          identifier: data.id,
          flag: true,
        });

        setSoftRefresh(true);
      } else if (event === "fileError") {
        decreaseCounter();
        // The Uploader guarantees error is a non-empty, user-safe string.
        const { file, error } = data;
        toast.fail(error);
        uploadingRef.current = false;
        setUploadingError({
          uniqueIdentifier: file.uniqueIdentifier,
          error: error,
        });
      }
    },
    [
      showUploadProgress,
      callRegisterMediaOnCms,
      emptyMediaDetails,
      emptyAssetDetails,
      getMedia,
      openUpload,
      closeModal,
      updateFileProgress,
      decreaseCounter,
      setUploadedSuccessfully,
      setSoftRefresh,
    ]
  );

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (!uploadingRef.current) return;
      event.preventDefault();
      const message =
        "Your file upload is in progress. Are you sure you want to leave?";
      event.returnValue = message;
      window.addEventListener("unload", sendCancelUploadRequest);
      return message;
    },
    [sendCancelUploadRequest]
  );

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalTitle("");
    setModalContent(null);
    setModalFooter(null);
    emptyMediaDetails();
    emptyAssetDetails();
    closeModal();
  }, [emptyMediaDetails, emptyAssetDetails, closeModal]);

  const handleNextClick = useCallback(() => {
    const event = new CustomEvent("customNextClick");
    window.dispatchEvent(event);
  }, []);

  const handleClick = useCallback(
    (option: string) => {
      trackCreate(option);

      if (option === "EMBED") {
        emptyMediaDetails();
        emptyAssetDetails();
        const embedContent = (
          <EmbedForm
            closeModal={handleCloseModal}
            openUpload={openUpload}
            setModalBtnDisabled={setModalBtnDisabled}
            onAssetTypeChange={onAssetTypeChange}
          />
        );
        const embedFooter = (
          <div className="d-flex justify-content-end">
            <Button
              type="primary"
              isDisabled={modalBtnDisabled}
              onClick={() => {
                const event = new CustomEvent("nextClickEmbed");
                window.dispatchEvent(event);
              }}
            >
              {footerText}
            </Button>
          </div>
        );
        updateModalContent?.(embedContent, "CREATE > Embed", embedFooter);
      } else if (option === "CUSTOM_UPLOADS") {
        const customContent = (
          <CustomUploadForm
            closeModal={handleCloseModal}
            openUpload={openUpload}
            updateActiveCustomUploadTab={updateActiveCustomUploadTab}
            onNextClick={handleNextClick}
            isNextDisabled={!hasFormChanges || isValidatingUrl}
            nextButtonText={isValidatingUrl ? "Validating..." : "Next"}
            onAssetTypeChange={onAssetTypeChange}
          />
        );
        const customFooter = (
          <div className="d-flex justify-content-end">
            <Button
              type="primary"
              isDisabled={isValidatingUrl}
              onClick={handleNextClick}
            >
              {isValidatingUrl ? "Validating..." : "Next"}
            </Button>
          </div>
        );
        updateModalContent?.(
          customContent,
          "CREATE > Custom Uploads",
          customFooter
        );
      } else if (option === "SIMULIVE") {
        const simuliveContent = <SimuliveForm closeModal={handleCloseModal} />;
        updateModalContent?.(simuliveContent, "CREATE > Simulive");
      } else if (option === "TEXT_TO_SPEECH") {
        const elevenLabs = TTS_SERVICES.find((s) => s.id === "elevenlabs")!;
        const ttsContent = (
          <TextToSpeechForm
            closeModal={handleCloseModal}
            openUpload={openUpload}
            initialService={elevenLabs}
            onNextDisabledChange={onTtsNextDisabledChange}
            onNextPendingChange={onTtsRpcPendingChange}
          />
        );
        updateModalContent?.(ttsContent, "CREATE > Text to Speech");
      } else if (option === "PREMIERE") {
        const premiereContent = (
          <PremiereForm
            closeModal={handleCloseModal}
            openUpload={openUpload}
            setModalBtnDisabled={onPremiereNextDisabledChange}
            setRpcPending={onPremiereRpcPendingChange}
            onAssetTypeChange={onAssetTypeChange}
            onPremiereReady={(data: any) => {
              openPremiereEditor?.(data);
            }}
          />
        );
        updateModalContent?.(premiereContent, "CREATE > Premiere");
      } else if (option === "BULK_UPLOAD") {
        updateModalContent?.(
          <BulkUploadForm
            closeModal={handleCloseModal}
            onUploadsStarted={(items) => {
              handleCloseModal();
              onBulkUploadsStarted?.(items);
            }}
          />,
          "CREATE > Bulk Upload"
        );
      }
    },
    [
      updateModalContent,
      openUpload,
      openPremiereEditor,
      onBulkUploadsStarted,
      onPremiereNextDisabledChange,
      onPremiereRpcPendingChange,
      handleCloseModal,
      onAssetTypeChange,
      modalBtnDisabled,
      footerText,
      updateActiveCustomUploadTab,
      handleNextClick,
      hasFormChanges,
      isValidatingUrl,
      onTtsNextDisabledChange,
      onTtsRpcPendingChange,
      emptyMediaDetails,
      emptyAssetDetails,
    ]
  );

  // Handle initial option selection
  useEffect(() => {
    if (initialOption) {
      handleClick(initialOption);
    }
  }, [initialOption, handleClick]);

  const MAX_BUCKETS_VISIBLE = 16;

  const allEntries = useMemo(
    () =>
      resolveBucketEntries(
        createBucketOrder,
        (defaultTeam as any)?.bucket_order,
        userBucketsList
      ),
    [createBucketOrder, defaultTeam, userBucketsList]
  );

  // visibleOrderRef holds only the visible (capped) entries for drag-reorder
  visibleOrderRef.current = allEntries.slice(0, MAX_BUCKETS_VISIBLE);

  const renderSystemCard = (item: (typeof CREATE_OPTIONS)[number]) => {
    const isBulkUpload = item.id === "BULK_UPLOAD";
    const cardContent = (
      <div className="card-link create-item" style={{ position: "relative" }}>
        {isBulkUpload && (
          <span className="bulk-beta-badge" style={{ fontSize: "9px" }}>
            BETA
          </span>
        )}
        <item.Icon size={32} strokeWidth={1.5} />
        <span className="mt-1 mb-1">{item.title}</span>
      </div>
    );
    let btnInner: React.ReactNode = cardContent;
    if (item.type === "uploader") {
      btnInner = (
        <Uploader
          id={item.id}
          type={item.asset_type}
          onFileEvent={onFileEvent}
          datatype={item.datatype || ""}
        >
          <div onClick={() => trackCreate(item.id)}>{cardContent}</div>
        </Uploader>
      );
    }
    return (
      <div
        className="create-btn bg-white"
        role="button"
        onClick={
          item.type === "uploader" ? undefined : () => handleClick(item.id)
        }
      >
        {btnInner}
      </div>
    );
  };

  const renderCustomCard = (bucket: BucketModel) => (
    <BucketCard
      bucket={bucket}
      onFileEvent={onFileEvent}
      index={0}
      onCardClick={(b) => trackCreate(b.name || "bucket")}
    />
  );

  const renderEntryInner = (entry: (typeof allEntries)[number]) =>
    entry.kind === "system"
      ? renderSystemCard(entry.item)
      : renderCustomCard(entry.item);

  const getEntryLabel = (entry: (typeof allEntries)[number]) =>
    entry.kind === "system" ? entry.item.title || "" : entry.item.name || "";

  const trimmedQuery = searchQuery.trim().toLowerCase();
  const visibleEntries = trimmedQuery
    ? allEntries.filter((e) =>
        getEntryLabel(e).toLowerCase().includes(trimmedQuery)
      )
    : allEntries.slice(0, MAX_BUCKETS_VISIBLE);
  const noResults = trimmedQuery.length > 0 && visibleEntries.length === 0;

  useEffect(() => {
    updateModalFooter?.(
      <div className="d-flex justify-content-end">
        <button
          type="button"
          className="fast-uploader-btn"
          onClick={() => window.open(import.meta.env.VITE_FTP_URL, "_blank")}
        >
          <Zap size={14} strokeWidth={2} />
          Fast Uploader
        </button>
      </div>
    );
  }, [updateModalFooter]);

  return (
    <div className="">
      <style>
        {`
          .create-card-wrap { cursor: pointer; }
          .create-card-wrap:hover { cursor: grab; }
          .create-card-wrap:active { cursor: grabbing; }
          .create-card-wrap:hover .drag-hint-icon { opacity: 1 !important; }
          .bulk-beta-badge {
            position: absolute;
            top: 6px;
            right: 8px;
            font-size: 3px;
            font-weight: 500;
            letter-spacing: 0.06em;
            color: #007aff;
            background: none;
            border: none;
            padding: 0;
            line-height: 1;
            transition: color 0.3s;
            pointer-events: none;
          }
          .card-link:hover .bulk-beta-badge {
            color: #fff;
          }
          .fast-uploader-btn {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: none;
            border: none;
            padding: 0;
            font-size: 14px;
            // font-weight: 600;
            color: #007aff;
            cursor: pointer;
            transition: color 0.15s ease;
          }
          .fast-uploader-btn:hover {
            color: #0056cc;
            text-decoration: underline;
          }
        `}
      </style>
      {noResults ? (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: "120px", fontSize: "13px", color: "#868e96" }}
        >
          No buckets match "{searchQuery}"
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={visibleOrderRef.current.map((it) => it.id)}
            strategy={rectSortingStrategy}
          >
            <div
              className="d-flex flex-wrap justify-content-center pt-1"
              style={{ gap: "31px" }}
            >
              {visibleEntries.map((entry: BucketEntry) => (
                <SortableCard
                  key={entry.id}
                  id={entry.id}
                  disabled={entry.id === "BULK_UPLOAD"}
                >
                  {renderEntryInner(entry)}
                </SortableCard>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  mediaList: state.media.mediaList,
  activeCustomUploadTab: state.media.activeCustomUploadTab,
  adminBucketList: state.media.adminBucketList.list,
  defaultTeam: state.constants.defaultTeam,
  createBucketOrder: state.users.createBucketOrder,
});
const mapDispatch = {
  callRegisterMediaOnCms,
  updateFileProgress,
  showUploadProgress,
  setUploadedSuccessfully,
  setSoftRefresh,
  decreaseCounter,
  getUpdateMedia,
  getMedia,
  emptyMediaDetails,
  emptyAssetDetails,
  updateIsFilter,
  setisFilter,
  setMediaList,
  updateMediaStatus,
  updateActiveCustomUploadTab,
  setCreateBucketOrder,
};
const connector = connect(mapState, mapDispatch);

interface IFileUploadOptions {
  closeModal: () => void;
  openUpload?: any;
  openPremiereEditor?: (data: any) => void;
  updateModalContent?: (
    content: React.ReactNode,
    title: string,
    footer?: React.ReactNode
  ) => void;
  updateModalFooter?: (footer: React.ReactNode) => void;
  onPremiereNextDisabledChange?: (disabled: boolean) => void;
  onPremiereRpcPendingChange?: (pending: boolean) => void;
  onTtsNextDisabledChange?: (disabled: boolean) => void;
  onTtsRpcPendingChange?: (pending: boolean) => void;
  initialOption?: string;
  onAssetTypeChange?: (assetType: string) => void;
  externalSearchQuery?: string;
  onBulkUploadsStarted?: (items: BulkMediaItem[]) => void;
}

type Props = ConnectedProps<typeof connector> & IFileUploadOptions;
export default connector(CreateOptions);
